<template>
  <section class="pb-1">
    <b-row
      v-if="isLoading"
      class="mb-50"
    >
      <b-col
        sm="3"
        class="d-flex"
      >
        <b-skeleton
          v-for="index in 3"
          :key="'skeleton'+index"
          class="mr-1"
          height="33px"
          width="30%"
        />
      </b-col>
      <b-col
        sm="9"
        class="d-flex justify-content-end"
      >
        <b-skeleton
          v-for="index in 3"
          :key="'b-skeleton' + index"
          class="mr-1"
          height="34px"
          :width="index === 1 ? '25%' : '7.5%'"
        />
      </b-col>
    </b-row>
    <b-row v-else>
      <b-col sm="3">
        <b-button
          v-for="(interval, index) in wrapper.chartIntervals"
          :key="'chartInterval-'+index"
          :class="wrapper.filter.chartInterval === interval.value ? 'text-primary' : 'text-secondary'"
          variant="link"
          class="mb-2 mr-1"
          size="sm"
          @click="setChartInterval(interval.name)"
        >
          {{ interval.name }}
        </b-button>
      </b-col>
      <b-col
        sm="9"
        class="d-flex justify-content-end"
      >
        <filter-wrapper
          v-if="selectedFilters.length > 0"
          :properties="selectedFilters"
          :width-offset="850"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <b-dropdown
          ref="dateDropdown"
          menu-class="py-0 daterangepicker-menu"
          right
          class="mr-1"
          variant="outline-dark"
          size="sm"
          no-caret
        >
          <template #button-content>
            {{ dateRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              :ranges="predefinedRanges"
              default-range-select="defaultRange"
              :allow-compare="false"
              @submit="submitDateRange"
              @cancel="closeDropdown"
            />
          </div>
        </b-dropdown>
        <dropdown
          :title="$tk('ClientPortal.Admin.Dashboard.Filters')"
          class="mb-1 mr-1"
          :show-dropdown="showFilters"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in wrapper.availableFilters"
              :key="filter.name + '-' + index"
              :input-id="filter.displayName"
              :text="filter.displayName"
              input-name="submenu"
              :selected-filter="selectedFilter(filter.displayName)"
              :checked="menu"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <dropdown-search-item
                  v-if="filter.availableOptions.length > 10"
                  :key="'search-'+filter.name"
                  :data="filter"
                />
                <dropdown-item
                  v-for="(options, optionIndex) in filter.availableOptions.filter(option => option.key.toLowerCase().startsWith(filter.search)).slice(0, 100)"
                  :key="options.value.optionCode + '-' + optionIndex"
                  :label="options.key"
                  :filter-name="filter.name"
                  :value="options.value.optionCode"
                  :menu-name="filter.displayName"
                  :selected="options.value.selected"
                  @change="includeOrExclude"
                />
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter.displayName, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearFilters"
            />
          </template>
        </dropdown>
        <b-button
          variant="primary"
          class="mb-2"
          size="sm"
          @click="loadAllStatistics"
        >
          {{ $tk('ClientPortal.Admin.Dashboard.Apply') }}
        </b-button>
      </b-col>
    </b-row>
    <chart
      :key="`prebidAdapters-${isLoading}`"
      chart-type="pie"
      :chart-data="prebidStatsData.prebidAdapters"
      :colors="colors"
      :is-loading="isLoading"
      :title="$tk('ClientPortal.Admin.Prebid.PrebidAdapters')"
    />
    <chart
      :key="`totalNumberOfBids-${isLoading}`"
      :chart-data="prebidStatsData.totalNumberOfBids"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :colors="colors"
      icon="ClientPortal.Admin.PrebidDashboard.BidsLost.Info"
      :title="$tk('ClientPortal.Admin.Prebid.TotalNumberOfBids')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :contains-data="false"
            :text="$tk('ClientPortal.Admin.Prebid.TotalBids')"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :key="`bidsWon-${isLoading}`"
      :chart-data="prebidStatsData.bidsWon"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :colors="colors"
      icon="ClientPortal.Admin.PrebidDashboard.BidsLost.Info"
      :title="$tk('ClientPortal.Admin.Prebid.BidsWon')"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :contains-data="false"
            :text="$tk('ClientPortal.Admin.Prebid.TotalBids')"
          />
        </b-row>
      </template>
    </chart>
    <chart
      :key="`bidsLost-${isLoading}`"
      :chart-data="prebidStatsData.bidsLost"
      :wrapper="wrapper"
      :is-loading="isLoading"
      :colors="colors"
      :title="$tk('ClientPortal.Admin.Prebid.BidsLost')"
      icon="ClientPortal.Admin.PrebidDashboard.BidsLost.Info"
    >
      <template slot="header">
        <b-row>
          <StatisticsGraphInfo
            :contains-data="false"
            :text="$tk('ClientPortal.Admin.Prebid.TotalBids')"
          />
        </b-row>
      </template>
    </chart>
    <b-card>
      <Table
        :data="adapterCodeTable"
        :columns="prebidColumns"
        :is-loading="isLoading"
        type-of-table="prebid"
        :enable-pagination="false"
        :enable-sort="false"
      />
    </b-card>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import store from '@/store'
import StatisticsGraph from '@/components/StatisticsGraph.vue'
import StatisticsGraphInfo from '@/components/StatisticsGraphInfo.vue'
import Table from '@/components/Table.vue'
import {
  ref,
  computed,
  onMounted,
  reactive,
} from '@vue/composition-api'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownSearchItem from '@/components/DropdownSearchItem.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import moment from 'moment'
import DateRangePicker from '@/components/VueDateRangePicker'
import translation from '@/mixins/translate'
import FilterWrapper from '@/components/FilterWrapper.vue'

export default {
  components: {
    GjIcon,
    chart: StatisticsGraph,
    StatisticsGraphInfo,
    Table,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    DropdownSearchItem,
    DateRangePicker,
    FilterWrapper,
    DropdownClearItem,
  },
  filters: {
    formatValue(amount) {
      const amt = Number(amount)
      // eslint-disable-next-line no-mixed-operators
      return amt && amt.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'
    },
  },
  mounted() {
    this.$root.$on('bv::dropdown::show', () => {
      this.toggleFilters(false)
    })
  },
  setup(_, context) {
    const prebidColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.AdapterCode'),
        field: 'adapterCode',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.TotalBids'),
        field: 'totalBids',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.AverageCPM'),
        field: 'averageCPM',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.MaxCPM'),
        field: 'maxCPM',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.MinCPM'),
        field: 'minCPM',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.Timeouts'),
        field: 'timeouts',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Prebid.AverageResponseTime'),
        field: 'averageResponseTime',
      },
    ]
    const predefinedRanges = {
      today: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Today'),
        startDate: moment(),
        endDate: moment(),
      },
      yesterday: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Yesterday'),
        startDate: moment().subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last7Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last7Days'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last30Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last30Days'),
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      lastMonth: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.LastMonth'),
        startDate: moment().subtract(1, 'months').startOf('month'),
        endDate: moment().subtract(1, 'month').endOf('month'),
      },
      defaultRange: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.DefaultRange'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment(),
      },
    }
    const isLoading = ref(true)
    const menu = ref('')
    const showFilters = ref(false)
    const filterSearchTerm = ref('')
    const wrapper = computed(() => store.getters.prebidDashboardModel)
    const adapterStats = computed(() => store.getters.adapterStats)
    const prebidStatsData = computed(() => store.getters.prebidStatsData)
    const adapterCodeTable = computed(() => store.getters.adapterCodeTable)
    const colors = ['#1D79F2', '#FFEC00', '#7c48e3', '#36B37E', '#FF0000', '#7CDDDD']
    const selectedFilters = reactive([])
    const dateRange = ref('')

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getPrebidDashboard')
      await store.dispatch('getAdapterStats', wrapper.value.filter)
      await store.dispatch('getPrebidStats', wrapper.value.filter)
      dateRange.value = wrapper.value.filter.dateRange
      isLoading.value = false
    })

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }

    const loadAllStatistics = async () => {
      toggleFilters(false)
      isLoading.value = true
      await store.dispatch('getAdapterStats', wrapper.value.filter)
      await store.dispatch('getPrebidStats', wrapper.value.filter)
      isLoading.value = false
    }

    const setChartInterval = interval => {
      if (wrapper.value.filter.chartInterval === interval) { return }

      wrapper.value.filter.chartInterval = interval
    }

    const closeDropdown = () => {
      context.refs.dateDropdown.hide(true)
    }

    const prependZero = value => {
      if (value.toString().length === 1) {
        return `0${value}`
      }
      return value
    }

    const formatDate = value => {
      const day = prependZero(value.getDate())
      const month = prependZero(value.getMonth() + 1)
      const year = prependZero(value.getFullYear())

      return `${day}/${month}/${year}`
    }

    const getDateFromMomentRange = (startMoment, endMoment) => {
      const startDate = new Date(startMoment)
      const endDate = new Date(endMoment)
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    const submitDateRange = range => {
      const { startDate, endDate } = range
      dateRange.value = getDateFromMomentRange(startDate, endDate)
      if (wrapper.value.filter.dateRange !== dateRange.value) { wrapper.value.filter.dateRange = dateRange.value }
      closeDropdown()
    }

    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
      filterSearchTerm.value = ''
    }

    const filterOptions = searchQuery => {
      filterSearchTerm.value = searchQuery
    }

    const updateSelectedFilters = (filter, count) => {
      const filterName = wrapper.value.availableFilters.find(f => f.name === filter)
      const filterObj = selectedFilters.find(f => f.name === filterName.displayName)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filterName.displayName)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filterName.displayName, selectedFilters: count })
      }
    }

    const includeOrExclude = (filter, option) => {
      const selectedOption = wrapper.value.availableFilters.find(f => f.name === filter).availableOptions.find(o => o.value.optionCode === option)
      const filters = wrapper.value.filter.includedFilters
      if (filter in filters) {
        const index = filters[filter].indexOf(option)
        if (index < 0) {
          filters[filter].push(option)
          selectedOption.value.selected = true
        } else {
          filters[filter].splice(index, 1)
          selectedOption.value.selected = false
        }
      } else {
        filters[filter] = []
        filters[filter].push(option)
        selectedOption.value.selected = true
      }
      updateSelectedFilters(filter, filters[filter].length)
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        wrapper.value.filter.includedFilters = {}
        wrapper.value.availableFilters.forEach(f => {
          const selectedFilter = f.availableOptions.filter(option => option.value.selected === true)
          selectedFilter.forEach(selectedOption => {
            selectedOption.value.selected = false
          })
        })
        toggleFilters(false)
      } else {
        const filterIndex = selectedFilters.findIndex(f => f.name === filter)
        const filterSelected = wrapper.value.availableFilters.find(f => f.displayName === filter)
        filterSelected.availableOptions.filter(option => option.value.selected === true).forEach(o => { o.value.selected = false })
        selectedFilters.splice(filterIndex, 1)
        delete wrapper.value.filter.includedFilters[filter]
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      menu.value = ''
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        toggleFilters(false)
        menu.value = ''
      }
    }

    return {
      wrapper,
      adapterStats,
      prebidStatsData,
      isLoading,
      adapterCodeTable,
      colors,
      menu,
      changeMenu,
      filterSearchTerm,
      filterOptions,
      includeOrExclude,
      selectedFilters,
      loadAllStatistics,
      setChartInterval,
      dateRange,
      predefinedRanges,
      submitDateRange,
      closeDropdown,
      clearFilters,
      toggleFilters,
      showFilters,
      prebidColumns,
      selectedFilter,
      openSelectedFilter,
    }
  },
}
</script>
